import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatWalletBalanceMinor } from '../../services/channelService';
import { listWallets } from '../../services/walletPublicService';
import { getAxiosErrorMessage, getWelcomeGreeting, logoutRemote } from '../../services/authService';
import WalletDashboardVpnActions from './WalletDashboardVpnActions';

function WalletBalancesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWallets();
      const list = Array.isArray(data.wallets) ? data.wallets : [];
      setRows(list);
    } catch (e) {
      console.error(e);
      setError(getAxiosErrorMessage(e, 'Failed to load wallets'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const distinctCurrencies = [...new Set(rows.map((r) => r.currency).filter((c) => c != null && String(c).trim() !== ''))];
  const canCombineMoneyTotals = distinctCurrencies.length <= 1;
  const totalsCurrency = distinctCurrencies[0] || 'INR';

  const totalBalanceMinor = rows.reduce((acc, r) => acc + (Number(r.balance) || 0), 0);
  const totalCreditLineLimitMinor = rows.reduce((acc, r) => acc + (Number(r.credit_line_limit_minor) || 0), 0);
  const totalCreditLineUsedMinor = rows.reduce(
    (acc, r) => acc + (Number(r.credit_line_depleted_minor) || 0),
    0
  );

  const hasCashCreditWallet = useMemo(
    () => rows.some((r) => String(r?.currency ?? '').trim().toUpperCase() === 'CASH'),
    [rows]
  );

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutRemote();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  return (
    <div>
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{getWelcomeGreeting()}</p>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <i className={`material-icons-outlined text-base ${loggingOut ? 'animate-spin' : ''}`}>
              {loggingOut ? 'refresh' : 'logout'}
            </i>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25">
              <i className="material-icons-outlined text-3xl">account_balance_wallet</i>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">Wallet dashboard</p>
              <h1 className="page-title">Your wallets</h1>
              <p className="page-subtitle">
                Select a wallet to open its transaction statement and exports.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
            <WalletDashboardVpnActions />
            {!loading && !error && hasCashCreditWallet ? (
              <Link
                to="/wallets/transfer"
                className="btn-primary inline-flex items-center gap-2 py-2 pl-2.5 pr-3 text-sm"
                title="Send credit from your wallet to someone who handed you physical cash (see cash credit note below)"
              >
                <i className="material-icons-outlined text-base">currency_exchange</i>
                Transfer credit
              </Link>
            ) : null}
            <button
              type="button"
              onClick={load}
              className="btn-secondary inline-flex items-center gap-2 py-2 pl-2.5 pr-3 text-sm"
              disabled={loading}
            >
              <i className={`material-icons-outlined text-base ${loading ? 'animate-spin' : ''}`}>refresh</i>
              Refresh
            </button>
          </div>
        </div>
      </header>

      {!loading && !error && hasCashCreditWallet && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/90 px-4 py-3 text-sm text-slate-700 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cash credit (currency CASH)</p>
          <p className="mt-1.5 leading-relaxed">
            Each cash-on-delivery order is settled against your cash credit line, while you collect physical cash from the
            customer. When you hand that cash to your team lead, they transfer credit from their wallet to yours so the
            ledger matches the handoff. The same continues up the chain until the money is sent to the company&apos;s bank
            account—then the company issues repayment on the wallet. Every person in between works within the credit line
            issued to them.
          </p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="card-surface p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Wallets</p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">{rows.length}</p>
          </div>
          {canCombineMoneyTotals ? (
            <>
              <div className="card-surface p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Combined available balance ({totalsCurrency})
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-emerald-700">
                  {formatWalletBalanceMinor(totalBalanceMinor, totalsCurrency)}
                </p>
              </div>
              <div className="card-surface p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Combined credit limit ({totalsCurrency})
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">
                  {formatWalletBalanceMinor(totalCreditLineLimitMinor, totalsCurrency)}
                </p>
              </div>
              <div className="card-surface p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Combined credit in use ({totalsCurrency})
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-red-700">
                  {formatWalletBalanceMinor(totalCreditLineUsedMinor, totalsCurrency)}
                </p>
              </div>
            </>
          ) : (
            <div className="card-surface p-5 sm:col-span-2 xl:col-span-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Balances</p>
              <p className="mt-1 text-sm text-slate-600">
                Wallets use more than one currency. Combined totals are hidden; see each row below.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="card-surface overflow-hidden">
        {loading && (
          <div className="space-y-3 p-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="flex items-start gap-3 border-l-4 border-red-500 bg-red-50 p-6 text-red-800">
            <i className="material-icons-outlined shrink-0">error_outline</i>
            <div>
              <p className="font-medium">Could not load wallets</p>
              <p className="mt-1 text-sm opacity-90">{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead>
                <tr className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="whitespace-nowrap px-5 py-4">Wallet ID</th>
                  <th className="whitespace-nowrap px-5 py-4">Owner type</th>
                  <th className="whitespace-nowrap px-5 py-4">Owner ID</th>
                  <th className="whitespace-nowrap px-5 py-4">Currency</th>
                  <th className="whitespace-nowrap px-5 py-4 text-right">Available balance</th>
                  <th className="whitespace-nowrap px-5 py-4 text-right">Credit limit</th>
                  <th className="whitespace-nowrap px-5 py-4 text-right">Credit in use</th>
                  {hasCashCreditWallet ? (
                    <th className="whitespace-nowrap px-5 py-4 text-right">Credit transfer</th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={hasCashCreditWallet ? 8 : 7} className="px-5 py-16 text-center">
                      <i className="material-icons-outlined mb-2 text-4xl text-slate-200">savings</i>
                      <p className="text-slate-500">No wallets linked to this account.</p>
                    </td>
                  </tr>
                )}
                {rows.map((row, index) => {
                  const wid = row.wallet_id;
                  const rowCurrency = row.currency != null && String(row.currency).trim() !== '' ? String(row.currency).trim() : 'INR';
                  const isCash = rowCurrency.toUpperCase() === 'CASH';
                  const canOpen = wid != null && String(wid).trim() !== '';
                  return (
                    <tr
                      key={String(wid ?? index)}
                      role="button"
                      tabIndex={canOpen ? 0 : -1}
                      onClick={() => {
                        if (canOpen) navigate(`/wallets/${encodeURIComponent(String(wid).trim())}/statement`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (canOpen) navigate(`/wallets/${encodeURIComponent(String(wid).trim())}/statement`);
                        }
                      }}
                      className={`transition-colors hover:bg-slate-50/80 ${
                        canOpen ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                      }`}
                    >
                      <td className="max-w-xs break-all px-5 py-4 font-mono text-xs text-slate-600">
                        {wid ?? '—'}
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-medium text-slate-900">{row.owner_type ?? '—'}</span>
                      </td>
                      <td className="max-w-[12rem] break-all px-5 py-4 font-mono text-xs text-slate-600">
                        {row.owner_id != null && String(row.owner_id).trim() !== '' ? String(row.owner_id) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-700">
                        {rowCurrency.toUpperCase() === 'CASH' ? (
                          <span title="Cash credit account (COD)">CASH (credit)</span>
                        ) : (
                          (row.currency ?? '—')
                        )}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <span className="font-semibold tabular-nums text-emerald-700">
                          {formatWalletBalanceMinor(row.balance, rowCurrency)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-700">
                        {formatWalletBalanceMinor(row.credit_line_limit_minor, rowCurrency)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <span className="font-semibold tabular-nums text-red-700">
                          {formatWalletBalanceMinor(row.credit_line_depleted_minor, rowCurrency)}
                        </span>
                      </td>
                      {hasCashCreditWallet ? (
                        <td className="whitespace-nowrap px-5 py-4 text-right">
                          {isCash && canOpen ? (
                            <div className="flex flex-wrap justify-end gap-1.5">
                              <Link
                                to="/wallets/transfer"
                                onClick={(e) => e.stopPropagation()}
                                title="Send credit from your wallet (e.g. after you receive physical cash from someone downstream)"
                                className="inline-flex items-center gap-0.5 rounded-lg border border-emerald-200 bg-emerald-50/80 px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100/80"
                              >
                                <i className="material-icons-outlined text-sm">send</i>
                                Transfer out
                              </Link>
                              <Link
                                to={`/wallets/${encodeURIComponent(String(wid).trim())}/statement#cash-receive-qr`}
                                onClick={(e) => e.stopPropagation()}
                                title="Show QR so the person who received your physical cash can transfer credit to this wallet"
                                className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              >
                                <i className="material-icons-outlined text-sm">qr_code_2</i>
                                Receive in
                              </Link>
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WalletBalancesPage;
